import { type DataTableColumn, type DataTableSelection, DataTable } from '@/components/DataTable';
import { HintTooltip } from '@/components/HintTooltip';
import { PageHeader, PageShell } from '@/components/PageShell';
import { SpaceNames } from '@/components/SpaceNames';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSetSearchParam } from '@/lib/searchParams';
import {
    allBrandsState,
    brandRequestsState,
    joinAliasKeys,
    latestBrandResearchLookupState,
    modelsAwaitingAnyBrandRequestState,
    pendingBrandRequestCountState,
    sharedNameplateTableState,
} from '@/state';
import { useAsyncAtomValue, useFreshOnMountAtomValue, useUnwrappedAtomValue } from '@/state/loadableHooks';
import { useCurrentUserId } from '@/db/useCurrentUserId';
import { formatRelative } from '@/util/dates';
import logger from '@/util/logger';
import { suggestBrandRequestResolution } from '@rock/brands';
import { BrandRequestStatusSchema, isBrandResearchUnderway, USER_LIMIT_DEFAULTS } from '@rock/types';
import type {
    Brand,
    BrandRequest,
    BrandRequestId,
    BrandRequestResolutionKind,
    BrandRequestStatus,
    BrandRequestSuggestion,
    BrandResearch,
    BrandResearchProposal,
    UserId,
} from '@rock/types';
import { Loader2, Sparkles } from 'lucide-react';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BRAND_REQUESTS_LIMIT } from '@/state/brandRequests.state';
import { requestBrandResearch } from './brandResearchActions';
import { DECISION_LABELS } from './BrandResearchCard';
import { SuggestionChips } from './SuggestionChips';
import { waitingModelCounts } from './waitingModels';

const log = logger('BrandRequestsListPage');

export const STATUS_LABELS: Record<BrandRequestStatus, string> = {
    pending: 'Pending',
    parked: 'Parked',
    resolved: 'Resolved',
};

const EMPTY_DESCRIPTIONS: Record<BrandRequestStatus, string> = {
    pending: 'Nothing is waiting. New names appear here as equipment and recalls meet them.',
    parked: 'Nothing is parked. Park a name from its page when it isn’t worth deciding yet.',
    resolved: 'No resolved requests yet.',
};

/** Each way a request resolves: its label, and what it does to the name and the models waiting on it. */
export const RESOLUTION_KINDS: Record<BrandRequestResolutionKind, { label: string; hint: string }> = {
    brand: {
        label: 'Brand',
        hint: 'A brand of its own. Adds it to the catalog as a new brand; waiting models link to it, and their manual hunts can start.',
    },
    alias: {
        label: 'Alias',
        hint: 'Another name for a brand the catalog has. The name resolves to that brand from now on; waiting models link to it, merging into its model of the same number and type when it has one.',
    },
    line: {
        label: 'Line',
        hint: 'A product line of a cataloged brand, such as Hunter Brunswick. Works like an alias that also names the line; recalls, coverage and manuals still key on the brand.',
    },
    'shared-nameplate': {
        label: 'Shared nameplate',
        hint: 'A name two unrelated companies print. The bare name stops resolving to either; you name each side, and each waiting model goes to the side its space routes to, or is released when its space has no route.',
    },
    'not-a-brand': {
        label: 'Not a brand',
        hint: 'Noise, not a brand. The name stops being asked about; waiting models are released brandless, and their manual hunts can start.',
    },
};

/**
 * The names automation could not resolve, waiting for a human: new brand, alias, line, shared nameplate
 * or not a brand. Sorted by last sighting; the suggestion column is computed live against the catalog.
 */
export const BrandRequestsListPage: FC = () => {
    const [searchParams] = useSearchParams();
    const statusParam = BrandRequestStatusSchema.safeParse(searchParams.get('status'));
    const status: BrandRequestStatus = statusParam.success ? statusParam.data : 'pending';
    const setSearchParam = useSetSearchParam();
    const requests = useAsyncAtomValue(
        useMemo(() => brandRequestsState(status), [status]),
        [],
    );
    const brands = useAsyncAtomValue(allBrandsState, []);
    const sharedNameplates = useAsyncAtomValue(sharedNameplateTableState, []);
    const { value: pendingCount } = useFreshOnMountAtomValue(pendingBrandRequestCountState);
    // Hints are rows × brands × names of folding — once per data change, not once per render.
    const suggestionsById = useMemo(
        () =>
            new Map(
                requests.map((request) => [
                    request.id,
                    suggestBrandRequestResolution(request.brand, brands, sharedNameplates),
                ]),
            ),
        [requests, brands, sharedNameplates],
    );
    const waitingModels = useAsyncAtomValue(modelsAwaitingAnyBrandRequestState, []);
    const waitingCounts = useMemo(() => waitingModelCounts(waitingModels), [waitingModels]);
    const isOpen = status !== 'resolved';
    const researchById = useUnwrappedAtomValue(
        useMemo(
            () => latestBrandResearchLookupState(joinAliasKeys(isOpen ? requests.map((request) => request.id) : [])),
            [isOpen, requests],
        ),
    );
    const columns = useMemo(
        () =>
            columnsFor(
                brands,
                suggestionsById,
                isOpen ? waitingCounts : undefined,
                isOpen ? researchById : undefined,
                status === 'parked',
            ),
        [brands, suggestionsById, isOpen, waitingCounts, researchById, status],
    );
    const bulk = useBulkResearch(requests, researchById);

    return (
        <PageShell>
            <PageHeader
                title="Brand Requests"
                description="Names automation met that are not in the catalog. Decide what each one is; everything waiting on it links up when you do."
            >
                {isOpen && requests.length > 0 && <BulkResearchButtons bulk={bulk} />}
                <Select
                    value={status}
                    onValueChange={(value) => {
                        bulk.cancel();
                        setSearchParam('status', value === 'pending' ? undefined : value);
                    }}
                >
                    <SelectTrigger className="w-[180px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="pending">
                            {STATUS_LABELS.pending}
                            {pendingCount !== undefined ? ` (${pendingCount})` : ''}
                        </SelectItem>
                        <SelectItem value="parked">{STATUS_LABELS.parked}</SelectItem>
                        <SelectItem value="resolved">{STATUS_LABELS.resolved}</SelectItem>
                    </SelectContent>
                </Select>
            </PageHeader>
            <Card>
                <CardHeader>
                    <CardTitle>{STATUS_LABELS[status]}</CardTitle>
                    <CardDescription>
                        {requests.length === 0
                            ? EMPTY_DESCRIPTIONS[status]
                            : bulk.selecting
                              ? `Tick the names to research. About 5¢ a run, up to ${USER_LIMIT_DEFAULTS['brand-research']} a day.`
                              : requests.length >= BRAND_REQUESTS_LIMIT
                                ? `Showing the ${BRAND_REQUESTS_LIMIT} most recently seen.`
                                : status === 'parked'
                                  ? 'Set aside until they are seen again. A new home, model or recall naming one brings it back to Pending.'
                                  : 'Tap a row to see where the name was seen and decide what it is.'}
                    </CardDescription>
                </CardHeader>
                {requests.length > 0 && (
                    <CardContent>
                        <DataTable
                            columns={columns}
                            rows={requests}
                            rowKey={(request) => request.id}
                            rowHref={(request) => `/products/brand-requests/${encodeURIComponent(request.id)}`}
                            selection={isOpen ? bulk.selection : undefined}
                        />
                    </CardContent>
                )}
            </Card>
        </PageShell>
    );
};

interface BulkResearch {
    selecting: boolean;
    starting: boolean;
    chosen: BrandRequest[];
    selection: DataTableSelection<BrandRequest> | undefined;
    begin: () => void;
    cancel: () => void;
    confirm: () => Promise<void>;
}

/**
 * Run AI Research on many open requests at once: each gets the same `requested` run the request page's
 * button writes. A request with a run underway can't be picked, so a second click never doubles a run, and
 * select-all leaves out the ones already researched: running one again is a deliberate tick.
 */
function useBulkResearch(
    requests: BrandRequest[],
    researchById: Map<BrandRequestId, BrandResearch> | undefined,
): BulkResearch {
    const by = useCurrentUserId();
    const [selecting, setSelecting] = useState(false);
    const [starting, setStarting] = useState(false);
    const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

    const isSelectable = (request: BrandRequest) => !!researchById && !isResearchUnderway(researchById.get(request.id));
    const inSelectAll = (request: BrandRequest) => researchById?.get(request.id)?.status !== 'done';
    const chosen = requests.filter((request) => selected.has(request.id) && isSelectable(request));

    const cancel = () => {
        setSelecting(false);
        setSelected(new Set());
    };

    const confirm = async () => {
        if (!by || chosen.length === 0) return;
        setStarting(true);
        try {
            const failed = await startResearch(chosen, by);
            if (failed > 0) toast.error(`Unable to start research for ${failed} of ${pluralNames(chosen.length)}`);
            else toast.success(`Researching ${pluralNames(chosen.length)}`);
            cancel();
        } finally {
            setStarting(false);
        }
    };

    return {
        selecting,
        starting,
        chosen,
        selection: selecting ? { selected, onChange: setSelected, isSelectable, inSelectAll } : undefined,
        begin: () => setSelecting(true),
        cancel,
        confirm,
    };
}

/** Starts a run for each request; resolves to how many failed to start. */
async function startResearch(requests: BrandRequest[], by: UserId): Promise<number> {
    const results = await Promise.allSettled(requests.map((request) => requestBrandResearch(request, by)));
    results.forEach((result, index) => {
        if (result.status === 'rejected') {
            log.error('Brand research failed to start', { err: result.reason, requestId: requests[index]?.id });
        }
    });
    return results.filter((result) => result.status === 'rejected').length;
}

const BulkResearchButtons: FC<{ bulk: BulkResearch }> = ({ bulk }) =>
    bulk.selecting ? (
        <div className="flex items-center gap-2">
            <Button variant="outline" onClick={bulk.cancel} disabled={bulk.starting}>
                Cancel
            </Button>
            <Button
                variant="success"
                onClick={() => void bulk.confirm()}
                disabled={bulk.chosen.length === 0 || bulk.starting}
            >
                {bulk.starting && <Loader2 className="h-4 w-4 animate-spin" />}
                Confirm ({bulk.chosen.length})
            </Button>
        </div>
    ) : (
        <Button variant="outline" onClick={bulk.begin}>
            <Sparkles className="h-4 w-4" />
            Run AI Research…
        </Button>
    );

function columnsFor(
    brands: Brand[],
    suggestionsById: Map<BrandRequestId, BrandRequestSuggestion[]>,
    /** Per request, the models it holds up; absent on the Resolved view, where nothing waits. */
    waitingCounts: Map<BrandRequestId, number> | undefined,
    /** Per request, its latest research run; absent on the Resolved view. */
    researchById: Map<BrandRequestId, BrandResearch> | undefined,
    showParkedAt: boolean,
): DataTableColumn<BrandRequest>[] {
    const parked: DataTableColumn<BrandRequest>[] = showParkedAt
        ? [
              {
                  key: 'parked',
                  header: 'Parked',
                  className: 'min-w-[120px]',
                  cell: (request) => formatRelative(request.parked?.at),
              },
          ]
        : [];
    const waiting: DataTableColumn<BrandRequest>[] = waitingCounts
        ? [
              {
                  key: 'waiting',
                  header: 'Waiting models',
                  className: 'min-w-[130px]',
                  sortBy: (request) => waitingCounts.get(request.id) ?? 0,
                  cell: (request) => <WaitingModelCount count={waitingCounts.get(request.id) ?? 0} />,
              },
          ]
        : [];
    const researched: DataTableColumn<BrandRequest>[] = researchById
        ? [
              {
                  key: 'researched',
                  header: 'Researched',
                  className: 'min-w-[120px]',
                  cell: (request) => <ResearchBadge run={researchById.get(request.id)} brands={brands} />,
              },
          ]
        : [];
    return [
        {
            key: 'name',
            header: 'Name',
            className: 'min-w-[200px]',
            cellClassName: 'font-medium',
            cell: (request) => (
                <span className="flex items-center gap-2">
                    {request.brand}
                    <SeenAgainBadge request={request} />
                </span>
            ),
        },
        {
            key: 'suggestion',
            header: 'Looks like',
            className: 'min-w-[260px]',
            cell: (request) =>
                request.status === 'resolved' ? (
                    <ResolutionBadge request={request} brands={brands} />
                ) : (
                    <SuggestionChips suggestions={suggestionsById.get(request.id) ?? []} compact />
                ),
        },
        ...parked,
        {
            key: 'sightings',
            header: 'Seen',
            className: 'min-w-[160px]',
            cell: (request) => <SightingCounts request={request} />,
        },
        ...waiting,
        ...researched,
        {
            key: 'spaces',
            header: 'Spaces',
            className: 'min-w-[140px]',
            cell: (request) => <SpaceNames spaceIds={request.spaceIds} />,
        },
        {
            key: 'first',
            header: 'First seen',
            className: 'min-w-[120px]',
            cell: (request) => formatRelative(request.firstSeenAt),
        },
        {
            key: 'last',
            header: 'Last seen',
            className: 'min-w-[120px]',
            cell: (request) => formatRelative(request.lastSeenAt),
        },
    ];
}

/** Where a request's latest research run stands; nothing when it was never researched. */
const ResearchBadge: FC<{ run: BrandResearch | undefined; brands: Brand[] }> = ({ run, brands }) => {
    if (!run) return null;
    if (isResearchUnderway(run)) {
        return run.status === 'requested' ? (
            <Badge variant="outline">Requested</Badge>
        ) : (
            <Badge className="border-transparent bg-sky-100 text-sky-900">
                <Loader2 className="h-3 w-3 animate-spin" />
                Running
            </Badge>
        );
    }
    if (run.status === 'done' && run.proposal) {
        return (
            <HintTooltip hint={proposalHint(run.proposal, brands)}>
                <Badge className="border-transparent bg-emerald-100 text-emerald-900">Done</Badge>
            </HintTooltip>
        );
    }
    return (
        <HintTooltip hint={run.error ?? 'The run didn’t finish. Research again to retry.'}>
            <Badge className="border-transparent bg-rose-100 text-rose-900">Failed</Badge>
        </HintTooltip>
    );
};

/** The provisional models an open request holds up, as a pill; nothing when it holds none. */
const WaitingModelCount: FC<{ count: number }> = ({ count }) =>
    count === 0 ? null : (
        <HintTooltip hint="Provisional models waiting on this name. Their manual hunt waits until the request is resolved.">
            <Badge className="border-transparent bg-sky-100 text-sky-900">{count} waiting</Badge>
        </HintTooltip>
    );

/** A pending request that came back from Parked because the name was seen again; nothing otherwise. */
const SeenAgainBadge: FC<{ request: BrandRequest }> = ({ request }) => {
    const summary = seenAgainSummary(request);
    if (!summary) return null;
    return (
        <HintTooltip hint={summary}>
            <Badge className="border-transparent bg-violet-100 text-violet-900">Back</Badge>
        </HintTooltip>
    );
};

/** Why a once-parked request is back in the queue, in a sentence; undefined for any other request. */
export function seenAgainSummary(request: BrandRequest): string | undefined {
    const parked = request.parked;
    if (request.status !== 'pending' || !parked?.seenAgain) return undefined;
    const where = parked.seenAgain.source === 'recall' ? 'in a recall notice' : 'on a homeowner’s unit';
    return `Parked ${formatRelative(parked.at)}, then seen again ${where} ${formatRelative(parked.seenAgain.at)}.`;
}

export const SightingCounts: FC<{ request: BrandRequest }> = ({ request }) => {
    const parts = [
        request.sourceCounts.equipment ? `${request.sourceCounts.equipment} equipment` : undefined,
        request.sourceCounts.recall ? `${request.sourceCounts.recall} recall` : undefined,
        request.sourceCounts.admin ? `${request.sourceCounts.admin} admin` : undefined,
    ].filter((part): part is string => !!part);
    return <span>{parts.length ? parts.join(', ') : `${request.sightingCount}`}</span>;
};

export const ResolutionBadge: FC<{ request: BrandRequest; brands: Brand[] }> = ({ request, brands }) => {
    const resolution = request.resolution;
    if (!resolution) return <Badge variant="outline">Resolved</Badge>;
    const brand = resolution.brandId ? brands.find((candidate) => candidate.id === resolution.brandId) : undefined;
    const kind = RESOLUTION_KINDS[resolution.kind];
    return (
        <span className="flex items-center gap-2">
            <HintTooltip hint={kind.hint}>
                <Badge variant="secondary">{kind.label}</Badge>
            </HintTooltip>
            {brand && <span className="text-sm">{brand.displayName ?? brand.canonicalName}</span>}
        </span>
    );
};

function isResearchUnderway(run: BrandResearch | undefined): boolean {
    return !!run && isBrandResearchUnderway(run, new Date());
}

/** A finished run's decision in a few words, e.g. "Alias of Whirlpool". */
function proposalHint(proposal: BrandResearchProposal, brands: Brand[]): string {
    const label = DECISION_LABELS[proposal.decision];
    if (proposal.decision === 'new-brand') return [label, proposal.brandName].filter(Boolean).join(' ');
    const target = brands.find((brand) => brand.id === proposal.targetBrandId);
    const targetName = target ? (target.displayName ?? target.canonicalName) : proposal.targetBrandName;
    if (!targetName) return label;
    return proposal.decision === 'not-a-brand' ? `${label} (belongs to ${targetName})` : `${label} ${targetName}`;
}

function pluralNames(count: number): string {
    return `${count} ${count === 1 ? 'name' : 'names'}`;
}
